<script module lang="ts">
  /** Generous wall-clock allowance for the seven focus contents to mount and fetch their first page. */
  export const FOCUS_MOUNT_WAIT_DEADLINE_MS = 10_000;

  /**
   * Advance a bounded wait state. A refusal fires once after the named deadline; callers that
   * observe readiness may fit, but the focus mount keeps its original start time until teardown.
   */
  export function focusMountWaitStep(
    allMounted: boolean,
    waitedFrames: number,
    waitStartedAtMs: number | null,
    nowMs: number,
    deadlineRefused: boolean,
  ) {
    if (allMounted) {
      return {
        verdict: "READY", waitedFrames: 0, waitStartedAtMs: null,
        deadlineRefused: false, refuseNow: false,
      };
    }
    if (deadlineRefused) {
      return { verdict: "REFUSED", waitedFrames, waitStartedAtMs, deadlineRefused: true, refuseNow: false };
    }
    const nextFrame = waitedFrames + 1;
    const startedAtMs = waitStartedAtMs ?? nowMs;
    const elapsedMs = nowMs - startedAtMs;
    const refuseNow = elapsedMs > FOCUS_MOUNT_WAIT_DEADLINE_MS;
    return {
      verdict: refuseNow ? "REFUSED" : "WAITING",
      waitedFrames: nextFrame,
      waitStartedAtMs: startedAtMs,
      deadlineRefused: refuseNow,
      refuseNow,
    };
  }

  /** Preserve one mount deadline across shell arrival and native pane-height publication. */
  export function focusWaitDeadlineStep(
    missingPaneIds: readonly string[],
    waitStartedAtMs: number,
    nowMs: number,
    deadlineRefused: boolean,
  ) {
    if (missingPaneIds.length === 0) {
      return { verdict: "READY", deadlineRefused, refuseNow: false, retry: false, refusal: null };
    }
    const step = focusMountWaitStep(false, 0, waitStartedAtMs, nowMs, deadlineRefused);
    const expired = step.verdict === "REFUSED";
    return {
      verdict: expired ? "EXPIRED" : "WAITING",
      deadlineRefused: step.deadlineRefused,
      refuseNow: step.refuseNow,
      retry: step.verdict === "WAITING",
      refusal: expired
        ? `FOCUS WAIT EXPIRED — missing pane ids: ${missingPaneIds.join(", ")}`
        : null,
    };
  }

  export function measuredFocusElementHeight(
    element: { getBoundingClientRect: () => { height: number }; scrollHeight: number },
    label: string,
    budgetPx: number | null = null,
  ): number {
    const rectHeight = element.getBoundingClientRect().height;
    const measured = Math.ceil(Math.max(rectHeight, element.scrollHeight));
    if (!Number.isFinite(measured) || measured <= 0) {
      throw new Error(`FOCUS FIT REFUSED — ${label} has no measurable content height`);
    }
    if (budgetPx !== null && (!Number.isFinite(budgetPx) || budgetPx <= 0)) {
      throw new Error(`FOCUS FIT REFUSED — ${label} height budget ${String(budgetPx)} is not positive and finite`);
    }
    return budgetPx === null ? measured : Math.min(measured, Math.floor(budgetPx));
  }

  export function focusFitRefusalStep(error: unknown, refusalLogged: boolean) {
    const detail = error instanceof Error ? error.message : String(error);
    const message = detail.startsWith("FOCUS FIT REFUSED")
      ? detail
      : `FOCUS FIT REFUSED — ${detail}`;
    return { message, logNow: !refusalLogged, refusalLogged: true };
  }

  export function parseFocusPaneHeights(paneHeightsRaw: string | undefined): number[] | null {
    if (!paneHeightsRaw) return null;
    let paneHeights: unknown;
    try {
      paneHeights = JSON.parse(paneHeightsRaw);
    } catch (error) {
      throw new Error(`FOCUS FIT REFUSED — linked chart pane heights are not valid JSON: ${String(error)}`);
    }
    if (!Array.isArray(paneHeights) || paneHeights.length !== 3 ||
        paneHeights.some((value: unknown) => typeof value !== "number" || !Number.isFinite(value) || value <= 0)) {
      throw new Error(`FOCUS FIT REFUSED — linked chart pane census is not three positive heights: ${paneHeightsRaw}`);
    }
    return paneHeights as number[];
  }
</script>

<script lang="ts">
  import { onMount } from "svelte";
  import DealerRailPanel from "./DealerRailPanel.svelte";
  import ExpiryTidePanel from "./ExpiryTidePanel.svelte";
  import Gex2Panel from "./Gex2Panel.svelte";
  import MaximizeFrame from "./MaximizeFrame.svelte";
  import PrimaryChartPanel from "./PrimaryChartPanel.svelte";
  import {
    FOCUS_GAP_PX,
    FOCUS_PROPORTIONS,
    FOCUS_TIDE_ROW,
    fitFocusCells,
    fittedTargetHeight,
    focusChartMinimumHeight,
    measuredRailCardContentHeight,
    snapLadderScrollTop,
    ladderGlyphPhase,
    snapLadderViewportHeight,
    type FocusChartId,
    type FocusContentHeights,
    type FocusFit,
    type RailMeasurementNode,
  } from "./focusLayout";
  import type { LiveMarker } from "./livePriceMarker";
  import type { RungKey } from "./rungLadder";

  let {
    api,
    chart,
    rung = $bindable<RungKey>("1"),
    onRungChange = null,
    livePrice,
    chartWindow = null,
    onChartWindowChange = null,
    tick,
    onexit,
  }: {
    api: string;
    chart: FocusChartId;
    rung?: RungKey;
    onRungChange?: ((rung: RungKey) => void) | null;
    livePrice: LiveMarker;
    chartWindow?: { from: number; to: number } | null;
    onChartWindowChange?: ((range: { from: number; to: number } | null) => void) | null;
    tick: { price: number | null; ms: number | null; fresh: boolean };
    onexit: () => void;
  } = $props();

  let chartCell = $state<HTMLDivElement | null>(null);
  let fit = $state<FocusFit | null>(null);
  let fitRefusal = $state<string | null>(null);
  let chartTargetHeight = $state(FOCUS_PROPORTIONS.chart.minPx);
  let fitFrame = 0;
  let ladderSnapFrame = 0;
  let ladderScrollTimer: ReturnType<typeof setTimeout> | undefined;
  const LADDER_SCROLL_QUIET_MS = 150;
  let mountWaitFrames = 0;
  let mountWaitStartedAtMs: number | null = null;
  let mountDeadlineRefused = false;
  let fitRefusalLogged = false;

  $effect(() => onRungChange?.(rung));

  function focusContentsMounted(main: HTMLElement): boolean {
    return Boolean(
      chartCell?.querySelector<HTMLElement>("section.primary")
      && chartCell.querySelector<HTMLElement>(".scvd .host")
      && main.querySelectorAll<HTMLElement>(".focus-tide-cell > section").length === 4
      && main.querySelector<HTMLElement>('[data-focus-panel="Gex2Panel"] > section')
      && main.querySelector<HTMLElement>('[data-focus-panel="DealerRailPanel"] > section'),
    );
  }

  function missingFocusPaneIds(main: HTMLElement): string[] {
    const missing: string[] = [];
    if (!chartCell?.querySelector<HTMLElement>("section.primary")) missing.push("PrimaryChartPanel");
    if (!chartCell?.querySelector<HTMLElement>(".scvd .host")) missing.push("PrimaryChartPanel/native-pane-heights");
    const tideCount = main.querySelectorAll<HTMLElement>(".focus-tide-cell > section").length;
    for (let index = tideCount; index < 4; index += 1) missing.push(`ExpiryTidePanel/${index}`);
    if (!main.querySelector<HTMLElement>('[data-focus-panel="Gex2Panel"] > section')) missing.push("Gex2Panel");
    if (!main.querySelector<HTMLElement>('[data-focus-panel="DealerRailPanel"] > section')) {
      missing.push("DealerRailPanel");
    }
    return missing;
  }

  function measuredContent(main: HTMLElement): FocusContentHeights | null {
    const primary = chartCell?.querySelector<HTMLElement>("section.primary");
    const chartHost = chartCell?.querySelector<HTMLElement>(".scvd .host");
    const tidePanels = [...main.querySelectorAll<HTMLElement>(".focus-tide-cell > section")];
    const gex = main.querySelector<HTMLElement>('[data-focus-panel="Gex2Panel"] > section');
    const dealer = main.querySelector<HTMLElement>('[data-focus-panel="DealerRailPanel"] > section');
    if (!primary || !chartHost || tidePanels.length !== 4 || !gex || !dealer) {
      throw new Error("FOCUS FIT REFUSED — all seven focus panel contents must be mounted before measurement");
    }
    const paneHeights = parseFocusPaneHeights(chartHost.dataset.paneHeights);
    if (paneHeights === null) return null;
    const primaryChrome = Math.max(0, primary.scrollHeight - chartHost.getBoundingClientRect().height);
    const chartMinimum = focusChartMinimumHeight(primaryChrome);
    function measuredRailSectionContentHeight(section: HTMLElement): number {
      const ladderMeasures = [...section.querySelectorAll<HTMLElement>(".laddermeasure")];
      const ladders = ladderMeasures.flatMap((measure) =>
        [...measure.querySelectorAll<HTMLElement>(".ladderview")]);
      const margins = (element: HTMLElement) => {
        const style = getComputedStyle(element);
        return {
          marginTop: Number.parseFloat(style.marginTop),
          marginBottom: Number.parseFloat(style.marginBottom),
        };
      };
      const collect = (element: HTMLElement, forceContainer = false): RailMeasurementNode => {
        if (ladders.includes(element)) {
          return { kind: "ladder", scrollHeight: element.scrollHeight, ...margins(element) };
        }
        const style = getComputedStyle(element);
        const children = [...element.children].filter((child): child is HTMLElement =>
          child instanceof HTMLElement && getComputedStyle(child).display !== "none");
        if (!forceContainer && !ladders.some((ladder) => element.contains(ladder))) {
          return { kind: "leaf", height: element.getBoundingClientRect().height, ...margins(element) };
        }
        const frameHeight = Number.parseFloat(style.borderTopWidth) + Number.parseFloat(style.borderBottomWidth) +
          Number.parseFloat(style.paddingTop) + Number.parseFloat(style.paddingBottom);
        const gap = Number.parseFloat(style.rowGap);
        return {
          kind: "container",
          frameHeight,
          rowGap: Number.isFinite(gap) ? gap : 0,
          children: children.map((child) => collect(child)),
        };
      };
      return measuredRailCardContentHeight(collect(section, true));
    }
    return {
      chart: chartMinimum,
      vsplit: Math.ceil(paneHeights[2] as number),
      tides: tidePanels.map((panel, index) =>
        Math.max(FOCUS_PROPORTIONS.tide.minPx, measuredFocusElementHeight(panel, `tide ${index}`))) as
        [number, number, number, number],
      rail0dte: measuredRailSectionContentHeight(gex),
      railAllExpiry: measuredRailSectionContentHeight(dealer),
    };
  }

  onMount(() => {
    const main = chartCell?.closest("main");
    if (!(main instanceof HTMLElement)) {
      const refusal = focusFitRefusalStep(
        "FOCUS FIT REFUSED — focus cells have no owning main element",
        fitRefusalLogged,
      );
      fitRefusal = refusal.message;
      fitRefusalLogged = refusal.refusalLogged;
      if (refusal.logNow) console.error(`[FocusLayout] ${refusal.message}`);
      return;
    }
    const prior = {
      columns: main.style.gridTemplateColumns,
      rows: main.style.gridTemplateRows,
      gap: main.style.gap,
      overflow: main.style.overflow,
      areas: main.style.gridTemplateAreas,
    };
    mountWaitStartedAtMs = performance.now();
    const refuseFit = (error: unknown) => {
      const refusal = focusFitRefusalStep(error, fitRefusalLogged);
      fit = null;
      fitRefusal = refusal.message;
      fitRefusalLogged = refusal.refusalLogged;
      main.style.gridTemplateColumns = "minmax(0, 1fr)";
      main.style.gridTemplateRows = "max-content";
      main.style.gridTemplateAreas = "none";
      main.style.overflowX = "hidden";
      main.style.overflowY = "auto";
      if (refusal.logNow) console.error(`[FocusLayout] ${refusal.message}`);
    };
    const waitForFocus = (missingPaneIds: string[]) => {
      const wait = focusWaitDeadlineStep(
        missingPaneIds,
        mountWaitStartedAtMs!,
        performance.now(),
        mountDeadlineRefused,
      );
      mountDeadlineRefused = wait.deadlineRefused;
      if (wait.retry) return true;
      if (wait.refuseNow && wait.refusal) {
        refuseFit(wait.refusal);
      }
      return false;
    };
    const applyFit = () => {
      try {
        if (mountDeadlineRefused) return;
        const missingPaneIds = missingFocusPaneIds(main);
        if (!focusContentsMounted(main)) {
          const mountWait = focusMountWaitStep(
            false,
            mountWaitFrames,
            mountWaitStartedAtMs,
            performance.now(),
            mountDeadlineRefused,
          );
          mountWaitFrames = mountWait.waitedFrames;
          mountDeadlineRefused = mountWait.deadlineRefused;
          if (mountWait.verdict === "WAITING") {
            scheduleFit();
            return;
          }
          if (mountWait.refuseNow) {
            const refusal = `FOCUS WAIT EXPIRED — missing pane ids: ${missingPaneIds.join(", ")}`;
            refuseFit(refusal);
          }
          return;
        }
        const measured = measuredContent(main);
        if (measured === null) {
          if (waitForFocus(["PrimaryChartPanel/native-pane-heights"])) scheduleFit();
          return;
        }
        const next = fitFocusCells(window.innerWidth, window.innerHeight, measured);
        const primary = chartCell?.querySelector<HTMLElement>("section.primary");
        const chartHost = chartCell?.querySelector<HTMLElement>(".scvd .host");
        if (!primary || !chartHost) {
          throw new Error("FOCUS FIT REFUSED — linked chart target disappeared during measurement");
        }
        const primaryStyle = getComputedStyle(primary);
        const primaryBorderHeight = Number.parseFloat(primaryStyle.borderTopWidth) +
          Number.parseFloat(primaryStyle.borderBottomWidth);
        chartTargetHeight = fittedTargetHeight(
          next.chart.h,
          primary.scrollHeight,
          chartHost.clientHeight,
          primaryBorderHeight,
        );
        fit = next;
        fitRefusal = null;
        main.style.overflowX = "hidden";
        main.style.overflowY = next.stacked ? "auto" : "hidden";
        main.style.gridTemplateColumns = next.stacked
          ? "minmax(0, 1fr)"
          : next.folded ? "repeat(2, minmax(0, 1fr))"
          : `repeat(4, minmax(0, 1fr)) ${next.rail0dte.w}px`;
        main.style.gridTemplateRows = next.stacked
          ? `${next.chart.h}px repeat(4, ${next.tides[0].h}px) auto`
          : next.folded ? `${next.chart.h}px repeat(2, ${next.tides[0].h}px) auto`
          : `${next.chart.h}px ${next.tideRow.h}px`;
        main.style.gridTemplateAreas = next.stacked ? "none" : "";
        main.style.gap = `${FOCUS_GAP_PX}px`;
        scheduleLadderSnap();
      } catch (error) {
        refuseFit(error);
      }
    };
    // The GEX ladder paints after its own fetch, often after the fit's frame: the snap is its own
    // schedule so the ladder observer below can re-run it without refitting the whole composition.
    const scheduleLadderSnap = () => {
      cancelAnimationFrame(ladderSnapFrame);
      ladderSnapFrame = requestAnimationFrame(() => {
          try {
            for (const ladder of main.querySelectorAll<HTMLElement>(
              '[data-focus-panel="Gex2Panel"] .ladderview',
            )) {
              ladder.style.removeProperty("height");
              ladder.style.removeProperty("max-height");
              ladder.style.removeProperty("flex");
              const rows = [...ladder.querySelectorAll<SVGGraphicsElement>("text.strike")];
              if (rows.length < 2) continue;
              const first = rows[0].getBoundingClientRect();
              const second = rows[1].getBoundingClientRect();
              const rowHeight = Math.abs(second.top - first.top);
              const availableHeight = ladder.clientHeight;
              const snappedHeight = snapLadderViewportHeight(availableHeight, rowHeight);
              if (snappedHeight <= 0) {
                throw new RangeError(
                  `FOCUS FIT REFUSED — ladder has ${availableHeight}px for ${rowHeight}px rows`,
                );
              }
              ladder.style.height = `${snappedHeight}px`;
              ladder.style.maxHeight = `${snappedHeight}px`;
              ladder.style.flex = `0 0 ${snappedHeight}px`;
              const maxScrollTop = Math.max(0, ladder.scrollHeight - ladder.clientHeight);
              // A strike glyph overhangs its slot, so the scroll edge snaps to the glyph grid's phase.
              const phase = ladderGlyphPhase(
                first.bottom - ladder.getBoundingClientRect().top + ladder.scrollTop, rowHeight,
              );
              const snappedScrollTop = snapLadderScrollTop(ladder.scrollTop, rowHeight, maxScrollTop, phase);
              ladder.scrollTop = snappedScrollTop;
              ladder.dataset.focusRowHeight = String(rowHeight);
              ladder.dataset.focusRowPhase = String(phase);
              ladder.dataset.focusViewportHeight = String(snappedHeight);
              ladder.dataset.focusScrollTop = String(snappedScrollTop);
            }
          } catch (error) {
            refuseFit(error);
          }
      });
    };
    const scheduleFit = () => {
      cancelAnimationFrame(fitFrame);
      fitFrame = requestAnimationFrame(applyFit);
    };
    const observer = new ResizeObserver(scheduleFit);
    observer.observe(main);
    for (const element of main.querySelectorAll<HTMLElement>(
      ".focus-chart section.primary, .focus-chart .scvd .host, .focus-tide-cell > section, .focus-rail-cell > section",
    )) {
      observer.observe(element);
    }
    const mutationObserver = new MutationObserver(scheduleFit);
    mutationObserver.observe(main, {
      subtree: true,
      attributes: true,
      attributeFilter: ["data-pane-heights"],
    });
    const ladderObserver = new MutationObserver(scheduleLadderSnap);
    for (const cell of main.querySelectorAll<HTMLElement>('[data-focus-panel="Gex2Panel"]')) {
      ladderObserver.observe(cell, { childList: true, subtree: true });
    }
    // Review r1 01a16dbf P2: a same-shape feed refresh re-centres the ladder by scrollTop alone (no
    // childList mutation), so any ladder scroll that is not the snap's own write re-snaps once quiet.
    const onLadderScroll = (event: Event) => {
      const ladder = event.target;
      if (!(ladder instanceof HTMLElement) || !ladder.matches('[data-focus-panel="Gex2Panel"] .ladderview')) return;
      if (String(ladder.scrollTop) === ladder.dataset.focusScrollTop) return;
      clearTimeout(ladderScrollTimer);
      ladderScrollTimer = setTimeout(scheduleLadderSnap, LADDER_SCROLL_QUIET_MS);
    };
    main.addEventListener("scroll", onLadderScroll, true);
    window.addEventListener("resize", scheduleFit);
    scheduleFit();
    return () => {
      cancelAnimationFrame(fitFrame);
      cancelAnimationFrame(ladderSnapFrame);
      clearTimeout(ladderScrollTimer);
      main.removeEventListener("scroll", onLadderScroll, true);
      observer.disconnect();
      mutationObserver.disconnect();
      ladderObserver.disconnect();
      window.removeEventListener("resize", scheduleFit);
      main.style.gridTemplateColumns = prior.columns;
      main.style.gridTemplateRows = prior.rows;
      main.style.gap = prior.gap;
      main.style.overflow = prior.overflow;
      main.style.gridTemplateAreas = prior.areas;
    };
  });
</script>

{#if fitRefusal}
  <div class="focus-fit-refusal" role="alert">{fitRefusal}</div>
{/if}

<div
  class="focus-cell focus-chart"
  class:focus-refused={fit === null}
  data-focus-area="chart"
  data-focus-panel="PrimaryChartPanel"
  data-focus-chart={chart}
  bind:this={chartCell}
  style:grid-column={fit?.stacked ? 1 : undefined}
  style:grid-row={fit?.stacked ? 1 : undefined}
  style:height={fit === null ? undefined : `${fit.chart.h}px`}
>
  <MaximizeFrame
    panelId={chart}
    panelClass="focus-primary"
    cellStyle=""
    maximized={true}
    hidden={false}
    ontoggle={onexit}
    fitSelector=".scvd .host"
    onfit={(height) => (chartTargetHeight = height)}
  >
    <div class="focus-chart-host">
      {#if chart === "primaryA"}
        <PrimaryChartPanel
          {api}
          chartId="primaryA"
          bind:rung
          limit={300}
          height={chartTargetHeight}
          focusHeight={chartTargetHeight}
          slot="CHART A · "
          stackMarker="CHART A LINKED STUDIES"
          chartRole="lower"
          rangeEnabled={false}
          activeWindow={chartWindow}
          tickPrice={tick.price}
          tickMs={tick.ms}
          tickFresh={tick.fresh}
        />
      {:else}
        <PrimaryChartPanel
          {api}
          chartId="primaryB"
          bind:rung
          limit={300}
          height={chartTargetHeight}
          focusHeight={chartTargetHeight}
          slot="CHART B · "
          stackMarker="CHART B LINKED STUDIES"
          chartRole="higher"
          rangeEnabled={false}
          onVisibleWindowChange={onChartWindowChange}
          tickPrice={tick.price}
          tickMs={tick.ms}
          tickFresh={tick.fresh}
        />
      {/if}
    </div>
  </MaximizeFrame>
</div>

<!-- FOCUSFIT: the separate V-split cell is retired; pane 2 belongs to the linked chart above. -->
{#each FOCUS_TIDE_ROW as scope, index (scope.id)}
  <div
    class="focus-cell focus-tide-cell"
    class:focus-refused={fit === null}
    data-focus-area="tide"
    data-focus-panel="ExpiryTidePanel"
    data-focus-scope={`${scope.tide === "index" ? "INDEX" : "ETF"}/${scope.expiration === "zero_dte" ? "0DTE" : "W"}`}
    data-focus-chart={chart}
    style:grid-column={fit?.stacked ? 1 : fit?.folded ? index % 2 + 1 : index + 1}
    style:grid-row={fit?.stacked ? index + 2 : fit?.folded ? Math.floor(index / 2) + 2 : 2}
    style:height={fit === null ? undefined : `${fit.tides[index].h}px`}
  >
    <ExpiryTidePanel
      tide={scope.tide}
      title={`${scope.tide === "index" ? "INDEX" : "ETF"} ${scope.expiration === "zero_dte" ? "0DTE" : "WEEKLY"} TIDE`}
      initialScope={scope.expiration}
      pinnedScope={true}
    />
  </div>
{/each}

<div
  class="focus-rail"
  class:focus-refused={fit === null}
  style:grid-column={fit?.stacked ? 1 : undefined}
  style:grid-row={fit?.stacked ? 6 : undefined}
  style:grid-template-rows={fit === null ? undefined : `${fit.rail0dte.h}px ${fit.railAllExpiry.h}px`}
>
  <div
    class="focus-cell focus-rail-cell"
    class:focus-refused={fit === null}
    data-focus-area="rail"
    data-focus-panel="Gex2Panel"
    data-focus-scope="0DTE"
    style:height={fit === null ? undefined : `${fit.rail0dte.h}px`}
  >
    <Gex2Panel {livePrice} defaultView="0DTE" />
  </div>
  <div
    class="focus-cell focus-rail-cell"
    class:focus-refused={fit === null}
    data-focus-area="rail"
    data-focus-panel="DealerRailPanel"
    data-focus-scope="ALL_EXPIRY"
    style:height={fit === null ? undefined : `${fit.railAllExpiry.h}px`}
  >
    <DealerRailPanel {livePrice} fixedScope="all_expiry" />
  </div>
</div>

<svelte:window onkeydown={(event) => event.key === "Escape" && onexit()} />

<style>
  .focus-fit-refusal {
    position: relative;
    grid-column: 1 / -1;
    grid-row: 1;
    z-index: 1;
    width: fit-content;
    justify-self: center;
    max-width: calc(100vw - 28px);
    border: 1px solid var(--stale);
    border-radius: var(--radius-chip);
    padding: 7px 10px;
    color: var(--stale-ink);
    background: var(--stale-soft);
    font: 700 11px/1.35 var(--font-num);
  }

  .focus-cell {
    min-width: 0;
    min-height: 0;
    overflow: hidden;
  }

  .focus-rail.focus-refused { grid-area: auto; grid-column: 1; }
  .focus-cell.focus-refused {
    grid-area: auto;
    grid-column: 1 !important;
    grid-row: auto !important;
    height: auto !important;
    min-height: max-content;
  }

  .focus-rail.focus-refused {
    height: auto !important;
    grid-template-rows: max-content max-content !important;
  }

  .focus-chart { grid-area: chart; }
  /* HUD-390-CLOSED-HEADER-1: the frame fills the cell, so MaximizeFrame's fit measures the cell as applyFit does. */
  .focus-chart > :global(.maximize-frame) { height: 100%; }
  .focus-chart-host { min-width: 0; height: 100%; }
  .focus-chart :global(.rungpills) { display: inline-flex; }
  .focus-tide-cell { min-width: 0; }

  .focus-rail {
    grid-area: rail;
    display: grid;
    align-content: start;
    gap: 14px;
    min-width: 0;
    min-height: 0;
  }

  .focus-cell :global(section) {
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }

  .focus-tide-cell :global(section) { height: inherit; }

  .focus-rail-cell :global(section) {
    height: 100%;
    min-height: 0;
  }

  .focus-rail-cell :global(.body),
  .focus-rail-cell :global(.laddermeasure) {
    display: flex;
    min-height: 0;
    flex: 1 1 auto;
    flex-direction: column;
    overflow: hidden;
  }

  .focus-rail-cell :global(.ladderview) {
    min-height: 0;
    max-height: 100%;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .focus-tide-cell :global(.card) {
    display: flex;
    min-height: 0;
    flex-direction: column;
  }

  .focus-tide-cell :global(.panel-id) {
    padding-top: var(--radius-card);
    padding-left: var(--radius-card);
  }

  .focus-tide-cell :global(.scope-grid) {
    min-height: 0;
    flex: 1 1 auto;
    overflow: hidden;
  }

  .focus-tide-cell :global(.scope) {
    height: 100%;
    max-height: 100%;
    overflow: hidden;
  }

  .focus-tide-cell :global(svg) {
    width: 100%;
    height: 136px;
  }
</style>
